import { useEffect, useMemo } from "react";
import { cloneDeep } from "es-toolkit/object";
import type {
  AssetTreeResponse,
  BaseTreeMetadata,
} from "src/queries/assets/assetsTree.types";
import { generateAtomHook } from "src/utils/jotai";
import type { AssetFilterKey } from "../../MetricsTable/filterModelAtom";
import type { TreeInput } from "../../NodeTree/components/NodeTreeNode/NodeTreeNode.props";
import { dfsTree } from "../../NodeTree/node-tree.utils";
import type { MetadataReducer, MetricsMetadataGetter } from "./AssetsTree.types";

interface FlatNodeTreeItem extends TreeInput {
  metadata: Record<string, unknown>;
  filter: Partial<{ [k in AssetFilterKey]: string }>;
}

export interface UseNodeTreeDataParams<T> {
  assetsData: any[] | null | undefined;
  metricsData: any[] | null | undefined;
  app_pit?: string;
  metadataMethods: {
    // todo rename to enrichAssetsWithMetricsMetadata
    getTransformedAssets?: MetricsMetadataGetter<T>;
    metadataReducer: MetadataReducer<T>;
  };
}

export interface UseNodeTreeDataReturn {
  nodeTree: TreeInput | null;
  flatNodeTree: Record<string, FlatNodeTreeItem>;
}

export const useNodeTreeHashMapState = generateAtomHook<Record<
  string,
  Omit<AssetTreeResponse<unknown>, "children">
> | null>(null);

export const useNodeTreeData = <T>(
  // nullable: the hook already bails on a missing tree / missing `path`
  assetsData: AssetTreeResponse<T> | null | undefined,
  metricsData: UseNodeTreeDataParams<T>["metricsData"],
  app_pit: UseNodeTreeDataParams<T>["app_pit"],
  metadataMethods: UseNodeTreeDataParams<T>["metadataMethods"]
): UseNodeTreeDataReturn => {
  const [, setNodeTreeToHashMap] = useNodeTreeHashMapState();

  const nodeTree = useMemo(() => {
    if (!assetsData || !assetsData.path) {
      return null;
    }

    const treeHashMap: Record<string, AssetTreeResponse<T>> = {};
    // cloneDeep (not structuredClone): asset tree nodes may include React elements in `title`
    const rootNode = cloneDeep(assetsData);
    const metaDataMap = metadataMethods?.getTransformedAssets?.(
      metricsData || [],
      app_pit
    );

    dfsTree<AssetTreeResponse<T>>(rootNode, (node) => {
      treeHashMap[node.path] = node;
      if (node.children) {
        node.metadata = [node, ...node.children].reduce(
          metadataMethods.metadataReducer,
          {
            ...(node.metadata ?? {}),
            ...(metaDataMap?.[node.path] ?? {}),
          }
        ) as T & BaseTreeMetadata;
      } else {
        node.metadata = {
          ...node.metadata,
          ...(metaDataMap?.[node.path] ?? {}),
        };
      }
    });

    return { rootNode, treeHashMap };
  }, [assetsData, metricsData, app_pit, metadataMethods]);

  useEffect(() => {
    nodeTree && setNodeTreeToHashMap(nodeTree?.treeHashMap ?? null);
  }, [nodeTree, setNodeTreeToHashMap]);

  // create a flat version of `nodeTree` where the keys are the unique ids
  const flatNodeTree = useMemo(() => {
    if (!nodeTree) return {};

    const flatMap: Record<string, FlatNodeTreeItem> = {};

    function add(obj: TreeInput) {
      // report duplicate ids
      if (flatMap[obj.path]) {
        console.warn("tree node with the same ID already exists:", obj.path, {
          existing: flatMap[obj.path],
          dup: obj,
        });
      }

      flatMap[obj.path] = obj as FlatNodeTreeItem;

      obj.children?.forEach((child) => add(child));
    }

    // shortcut: the tree is built from AssetTreeResponse (title may be ReactNode, filter is never set), upgrade by modelling that in TreeInput
    add(nodeTree.rootNode as unknown as TreeInput);

    return flatMap;
  }, [nodeTree]);

  return {
    nodeTree: nodeTree?.rootNode as unknown as TreeInput | null,
    flatNodeTree,
  };
};
